import { useCallback, useEffect, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";

import { addDaysToDate } from "@tm/insurance/utils";
import { convertDateToUnix, getIndiaTodayDate } from "../../../../utils";
import RangeDatePicker from "../../../RangeDatePicker/RangeDatePicker";
import ReactPortalComp from "../../../ReactPortalComp";
import type { InsuranceCalendarConfig } from "../../CommonLandingMweb.types";
import { COMMON_LANDING_CONSTANTS } from "../../constants";
import {
  SelectorTrackingSource,
  trackSelectorEvent,
} from "../../shared/MwebSelectorTracking";
import {
  Container,
  DateMonthText,
  DateSection,
  DayYearText,
  Diagonal,
  DurationBadge,
  DurationText,
  DurationWrap,
  HitArea,
} from "./Styles";

interface DateRangeSelectorProps {
  startDate?: Date;
  endDate?: Date;
  onDateChange?: (startDate: Date, endDate: Date) => void;
  isAnnualMultitrip?: boolean;
  /** Resolved from multitrip `tripOption.config` or selected plan segment `config` (PWA contract). */
  calendarConfig?: InsuranceCalendarConfig;
  /** Trip option `calenderSelectionType` (API spelling). */
  calenderSelectionType?: string;
  /** Portaled calendar z-index; default 10 (see RangeDatePicker). */
  zIndex?: number;
  /** When true, the field is display-only (no calendar sheet). */
  disabled?: boolean;
  /** Which page's analytics to fire. Defaults to common landing. */
  trackingSource?: SelectorTrackingSource;
}

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const getDisplayDate = (date?: Date) => {
  if (!date) return { dateMonth: "", dayYear: "" };
  const day = date.getDate();
  const month = MONTHS_SHORT[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2);
  const dayOfWeek = DAYS_SHORT[date.getDay()];
  return {
    dateMonth: `${day} ${month}`,
    dayYear: ` '${year}, ${dayOfWeek}`,
  };
};

export const DateRangeSelector = ({
  startDate,
  endDate,
  onDateChange,
  isAnnualMultitrip = false,
  calendarConfig,
  calenderSelectionType,
  zIndex,
  disabled = false,
  trackingSource = "common_landing",
}: DateRangeSelectorProps) => {
  const isSingleDate =
    Boolean(isAnnualMultitrip) && calenderSelectionType === "SINGLE";

  const {
    endDateErrorMessage = "Policy end date is not valid for this plan.",
    insPurchaseDateLimitInDays = 545,
    insPurchaseEndDateLimitInDays = 180,
    insPurchaseStartDateLimitInDays = 365,
    insPurchaseStartDateMinLimitInDays = 0,
    startDateErrorMessage = "Policy start date is not valid for this plan.",
    insPurchaseEndDateMinLimitInDays = 4,
    bottomBarDescription,
    bottomBarIcon,
    durationAllowed,
  } = calendarConfig ?? {};

  const minStartDate = useMemo(
    () =>
      addDaysToDate(
        getIndiaTodayDate(),
        insPurchaseStartDateMinLimitInDays ?? 0
      ),
    [insPurchaseStartDateMinLimitInDays]
  );

  const maxCalendarEndDate = useMemo(
    () => addDaysToDate(getIndiaTodayDate(), insPurchaseDateLimitInDays ?? 545),
    [insPurchaseDateLimitInDays]
  );

  const [sheetOpen, setSheetOpen] = useState(false);
  /** Shown in the collapsed row; updated on confirm and when parent props change. */
  const [committedStart, setCommittedStart] = useState<Date | undefined>(
    startDate
  );
  const [committedEnd, setCommittedEnd] = useState<Date | undefined>(endDate);
  const [range, setRange] = useState<DateRange | undefined>({
    from: startDate,
    to: endDate,
  });
  const [dateTab, setDateTab] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  useEffect(() => {
    setCommittedStart(startDate);
    setCommittedEnd(endDate);
  }, [startDate, endDate]);

  useEffect(() => {
    if (!sheetOpen) {
      setRange({ from: committedStart, to: committedEnd });
    }
  }, [committedStart, committedEnd, sheetOpen]);

  const duration =
    committedStart && committedEnd
      ? Math.max(
          0,
          Math.ceil(
            (committedEnd.getTime() - committedStart.getTime()) /
              (1000 * 60 * 60 * 24)
          )
        ) + 1 // Add 1 to include the start date
      : 0;

  const openSheet = () => {
    setRange({ from: committedStart, to: committedEnd });
    setDateTab(0);
    setShowToast(false);
    setSheetOpen(true);
  };

  const closeSheet = () => {
    setSheetOpen(false);
    setRange({ from: committedStart, to: committedEnd });
    setDateTab(0);
    setShowToast(false);
  };

  const handleDayClick = useCallback(
    (selectedDate: Date) => {
      const maxStartDate = addDaysToDate(
        getIndiaTodayDate(),
        insPurchaseStartDateLimitInDays
      );

      if (dateTab === 0) {
        if (maxStartDate && maxStartDate < selectedDate) {
          setShowToast(true);
          setToastMessage(startDateErrorMessage);
        } else if (range?.from && range?.to) {
          setRange({ from: selectedDate, to: undefined });
          setDateTab(1);
          setShowToast(false);
        }
      } else if (dateTab === 1) {
        if (range?.from) {
          const fromDate = new Date(range.from);
          const maxEndFromStart = addDaysToDate(
            fromDate,
            insPurchaseEndDateLimitInDays
          );
          if (range.from > selectedDate) {
            setRange({ from: selectedDate, to: undefined });
            setShowToast(false);
          } else if (maxEndFromStart && maxEndFromStart <= selectedDate) {
            setShowToast(true);
            setToastMessage(endDateErrorMessage);
          } else {
            setRange({ from: range.from, to: selectedDate });
            setShowToast(false);
            setDateTab(0);
          }
        }
      }
    },
    [
      dateTab,
      range?.from,
      range?.to,
      endDateErrorMessage,
      insPurchaseEndDateLimitInDays,
      insPurchaseStartDateLimitInDays,
      startDateErrorMessage,
    ]
  );

  const handleSingleDateSelect = useCallback(
    (selectedDate: Date) => {
      const maxStartDate = addDaysToDate(
        getIndiaTodayDate(),
        insPurchaseStartDateLimitInDays
      );

      if (maxStartDate && maxStartDate < selectedDate) {
        setShowToast(true);
        setToastMessage(startDateErrorMessage);
        return;
      }

      let autoEndDate: Date;
      if (durationAllowed && isSingleDate) {
        autoEndDate =
          addDaysToDate(selectedDate, durationAllowed) ?? selectedDate;
      } else {
        autoEndDate =
          addDaysToDate(selectedDate, insPurchaseEndDateMinLimitInDays ?? 4) ??
          selectedDate;
      }

      let end = autoEndDate;
      if (maxCalendarEndDate && maxCalendarEndDate < end) {
        end = maxCalendarEndDate;
      }

      setRange({ from: selectedDate, to: end });
      setShowToast(false);
    },
    [
      durationAllowed,
      insPurchaseEndDateMinLimitInDays,
      insPurchaseStartDateLimitInDays,
      isSingleDate,
      maxCalendarEndDate,
      startDateErrorMessage,
    ]
  );

  const handleSubmit = (chkIn?: Date, chkOut?: Date) => {
    if (chkIn && chkOut) {
      setCommittedStart(chkIn);
      setCommittedEnd(chkOut);
      onDateChange?.(chkIn, chkOut);
      trackSelectorEvent(trackingSource, "travel_date_selected", {
        payload: {
          departure:
            chkIn?.toLocaleDateString("en-GB")?.replace(/\//gi, ":") || "",
          arrival:
            chkOut?.toLocaleDateString("en-GB")?.replace(/\//gi, ":") || "",
        },
        searchContext: {
          fromDateTime: convertDateToUnix(chkIn),
          toDateTime: convertDateToUnix(chkOut),
        },
      });
    }
    setSheetOpen(false);
    setShowToast(false);
    setDateTab(0);
  };

  return (
    <>
      <HitArea
        type="button"
        onClick={disabled ? undefined : openSheet}
        disabled={disabled}
        aria-disabled={disabled}
        style={disabled ? { opacity: 0.65, cursor: "not-allowed" } : undefined}
      >
        <Container>
          <DateSection>
            <DateMonthText>
              {getDisplayDate(committedStart).dateMonth}
            </DateMonthText>
            <DayYearText>{getDisplayDate(committedStart).dayYear}</DayYearText>
          </DateSection>
          {duration > 0 ? (
            <DurationWrap>
              <Diagonal />
              <DurationBadge>
                <DurationText>
                  {duration}{" "}
                  {duration === 1
                    ? COMMON_LANDING_CONSTANTS.DAY
                    : COMMON_LANDING_CONSTANTS.DAYS}
                </DurationText>
              </DurationBadge>
            </DurationWrap>
          ) : null}
          <DateSection>
            <DateMonthText>
              {getDisplayDate(committedEnd).dateMonth}
            </DateMonthText>
            <DayYearText>{getDisplayDate(committedEnd).dayYear}</DayYearText>
          </DateSection>
        </Container>
      </HitArea>

      {sheetOpen ? (
        <ReactPortalComp>
          <RangeDatePicker
            calendarFooter
            calendarHeader
            onBackPress={closeSheet}
            onSubmitButtonPress={handleSubmit}
            range
            from={range?.from}
            to={range?.to}
            toastMessage={toastMessage}
            showToast={showToast}
            selected={range}
            handleDayClick={handleDayClick}
            maxEndDate={maxCalendarEndDate}
            minStartDate={minStartDate}
            isSingleDate={isSingleDate}
            bottomBarDescription={bottomBarDescription}
            bottomBarIcon={bottomBarIcon}
            onSingleDateSelect={handleSingleDateSelect}
            zIndex={zIndex}
          />
        </ReactPortalComp>
      ) : null}
    </>
  );
};
